import * as React from "react";

import { UploadCloud, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import Button from "@/components/button";

type AvatarUploaderProps = {
  /** Optional note to display below uploader */
  note?: string;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Current value can be an existing URL (from backend) or empty */
  value?: string | null;
  /** Called with a File (or null when removed) */
  onChange: (file: File | null) => void;
  /** Visual size of the circle */
  size?: "sm" | "md" | "lg" | "xl";
  /** Disable interaction */
  disabled?: boolean;
  /** Accept attribute for <input> */
  accept?: string;
  /** Show loader overlay */
  loading?: boolean;
};

const sizeMap = {
  sm: "w-24 h-24 text-xs", // 96x96
  md: "w-32 h-32 text-sm", // 128x128
  lg: "w-44 h-44 text-sm", // 160x160
  xl: "w-[200px] h-[200px] body-small",
} as const;

export default function AvatarUploader({
  value = null,
  onChange,
  size = "md",
  disabled,
  accept = "image/*",
  loading = false,
  note,
}: AvatarUploaderProps) {
  const { t } = useTranslation("common");
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(
    typeof value === "string" && value ? value : null,
  );
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const urlRef = React.useRef<string | null>(null);

  // If parent passes a new URL value, reflect it
  React.useEffect(() => {
    if (typeof value === "string") setPreviewUrl(value || null);
  }, [value]);

  // Revoke object URLs to avoid leaks
  React.useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  function openFileDialog() {
    if (disabled) return;
    inputRef.current?.click();
  }

  function handleFiles(files: FileList | null) {
    if (!files) return;
    const file = files[0];
    const url = URL.createObjectURL(file);
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setPreviewUrl(url);
    onChange(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }

  function handleRemove() {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPreviewUrl(null);
    onChange(null);
  }

  const ringClasses = dragOver
    ? "ring-2 ring-garnet border-garnet/50"
    : "ring-0";

  return (
    <div className="inline-flex flex-col items-center gap-3">
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openFileDialog();
          }
        }}
        onClick={openFileDialog}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        aria-disabled={disabled}
        className={[
          "relative rounded-full border border-dashed bg-[#f4f4f4] border-garnet",
          "flex items-center justify-center overflow-hidden",
          sizeMap[size],
          ringClasses,
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:bg-[#f4f4f4]",
        ].join(" ")}
      >
        {/* Loader overlay */}
        {typeof loading !== "undefined" && loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
            <span className="loader border-2 border-garnet border-t-transparent rounded-full w-10 h-10 animate-spin" />
          </div>
        )}
        {/* Hidden file input */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={disabled}
        />

        {/* Preview or placeholder */}
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt="Profile preview"
              className="h-full w-full object-cover rounded-full"
              draggable={false}
            />
            {!disabled && (
              <Button
                type="button"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="absolute right-1 top-1 h-7 w-7 rounded-full bg-white/90 backdrop-blur hover:bg-white shadow"
                aria-label={t("uploader.removeImage")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center text-garnet">
            <UploadCloud className="mb-1 h-5 w-5" />
            <span className="leading-tight">
              {t("uploader.uploadProfileImage")}
              <br />
              {t("uploader.uploadProfileImageLine2")}
            </span>
          </div>
        )}
      </div>
      {note && (
        <div className="text-xs text-gray-500 mt-1 text-center w-full max-w-xs">
          {note}
        </div>
      )}
    </div>
  );
}
